import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, AlertTriangle, ChevronDown, ChevronUp,
  FileText, Briefcase, ArrowRight
} from "lucide-react";
import { getOmieContratosOS, getOmieClientesStatus, postOmieClientesSincronizar } from "../../api";

function formatMoeda(valor) {
  if (valor === null || valor === undefined) return "—";
  return Number(valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function parseDataBR(str) {
  if (!str) return null;
  const [dia, mes, ano] = str.split("/");
  if (!dia || !mes || !ano) return null;
  const data = new Date(Number(ano), Number(mes) - 1, Number(dia));
  return Number.isNaN(data.getTime()) ? null : data;
}

function dentroDoIntervalo(dataStr, inicio, fim) {
  if (!inicio && !fim) return true;
  const data = parseDataBR(dataStr);
  if (!data) return false;
  if (inicio && data < new Date(inicio)) return false;
  if (fim && data > new Date(`${fim}T23:59:59`)) return false;
  return true;
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${color}`}>{icon}</div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
      </div>
    </div>
  );
}

function StatusBadge({ situacao }) {
  const ativo = situacao === "10";
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
      ativo ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
    }`}>
      {ativo ? "Ativo" : `Situação ${situacao ?? "—"}`}
    </span>
  );
}

function EmpresaBadge({ empresa }) {
  if (!empresa) return null;
  return (
    <span className="bg-indigo-50 text-indigo-700 text-xs font-semibold px-2 py-0.5 rounded-full">
      {empresa}
    </span>
  );
}

function OSLinha({ os }) {
  const cabecalho = os.Cabecalho || {};
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-2 text-sm font-medium text-gray-700">{cabecalho.cNumOS}</td>
      <td className="px-4 py-2"><EmpresaBadge empresa={os.empresa} /></td>
      <td className="px-4 py-2 text-sm text-gray-600">{os.cliente_nome || "—"}</td>
      <td className="px-4 py-2 text-sm text-gray-600">{os.vendedor_nome || "—"}</td>
      <td className="px-4 py-2 text-sm text-gray-600">{cabecalho.dDtPrevisao || "—"}</td>
      <td className="px-4 py-2 text-sm font-semibold text-gray-700">{formatMoeda(cabecalho.nValorTotal)}</td>
      <td className="px-4 py-2 text-sm">
        {os.InfoCadastro?.cFaturada === "S" ? (
          <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full">Faturada</span>
        ) : (
          <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">Pendente</span>
        )}
      </td>
    </tr>
  );
}

function ContratoCard({ contrato, osDataInicio, osDataFim }) {
  const [aberto, setAberto] = useState(false);
  const cabecalho = contrato.cabecalho || {};
  const osVinculadas = (contrato.os_vinculadas || []).filter((os) =>
    dentroDoIntervalo(os.Cabecalho?.dDtPrevisao, osDataInicio, osDataFim)
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setAberto((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-gray-50 transition"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="bg-gradient-to-br from-purple-900 to-pink-800 text-white p-2.5 rounded-xl shrink-0">
            <FileText className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-800 truncate">{cabecalho.cNumCtr}</p>
            <p className="text-xs text-gray-400 truncate">
              {contrato.cliente_nome || "Cliente não identificado"}
              {contrato.vendedor_nome ? ` · ${contrato.vendedor_nome}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <EmpresaBadge empresa={contrato.empresa} />
          <StatusBadge situacao={cabecalho.cCodSit} />
          <span className="text-sm font-semibold text-gray-700 hidden sm:inline">
            {formatMoeda(cabecalho.nValTotMes)}/mês
          </span>
          <span className="text-xs text-gray-400 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-md whitespace-nowrap">
            {osVinculadas.length} OS
          </span>
          {aberto ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {aberto && (
        <div className="border-t border-gray-100 px-6 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs text-gray-500">
            <div><span className="font-semibold text-gray-700">Vigência:</span> {cabecalho.dVigInicial} a {cabecalho.dVigFinal}</div>
            <div><span className="font-semibold text-gray-700">Dia faturamento:</span> {cabecalho.nDiaFat ?? "—"}</div>
            <div><span className="font-semibold text-gray-700">Cliente:</span> {contrato.cliente_nome || "—"}</div>
            <div><span className="font-semibold text-gray-700">Vendedor:</span> {contrato.vendedor_nome || "—"}</div>
          </div>

          {osVinculadas.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">Nenhuma OS vinculada a este contrato.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">OS</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendedor</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Previsão</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Valor</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Faturamento</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {osVinculadas.map((os) => (
                    <OSLinha key={`${os.empresa}-${os.Cabecalho?.nCodOS}`} os={os} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OmieContratosOS() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [dataInicioContrato, setDataInicioContrato] = useState("");
  const [dataFimContrato, setDataFimContrato] = useState("");
  const [dataInicioOS, setDataInicioOS] = useState("");
  const [dataFimOS, setDataFimOS] = useState("");
  const [status, setStatus] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);

  async function carregar() {
    setLoading(true);
    setErro(null);
    try {
      const res = await getOmieContratosOS();
      setDados(res);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function carregarStatus() {
    try {
      const res = await getOmieClientesStatus();
      setStatus(res);
      return res;
    } catch {
      return null;
    }
  }

  async function sincronizar() {
    setSincronizando(true);
    try {
      await postOmieClientesSincronizar();
      let res = await carregarStatus();
      while (res?.status === "executando") {
        await new Promise((r) => setTimeout(r, 3000));
        res = await carregarStatus();
      }
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSincronizando(false);
    }
  }

  useEffect(() => { carregar(); carregarStatus(); }, []);

  function limparFiltros() {
    setFiltro("");
    setDataInicioContrato("");
    setDataFimContrato("");
    setDataInicioOS("");
    setDataFimOS("");
  }

  const contratos = (dados?.contratos ?? []).filter((c) => c.cabecalho?.cCodSit === "10");
  const osSemContrato = dados?.os_sem_contrato ?? [];

  const q = filtro.trim().toLowerCase();
  const osFiltroAtivo = Boolean(dataInicioOS || dataFimOS);
  const contratosFiltrados = contratos.filter((c) => {
    const cabecalho = c.cabecalho || {};
    if (!dentroDoIntervalo(cabecalho.dVigFinal, dataInicioContrato, dataFimContrato)) return false;
    if (osFiltroAtivo) {
      const temOSNoPeriodo = (c.os_vinculadas || []).some((os) =>
        dentroDoIntervalo(os.Cabecalho?.dDtPrevisao, dataInicioOS, dataFimOS)
      );
      if (!temOSNoPeriodo) return false;
    }
    if (!q) return true;
    const alvo = [
      cabecalho.cNumCtr, c.cliente_nome, c.vendedor_nome,
      ...(c.os_vinculadas || []).map((os) => os.Cabecalho?.cNumOS),
    ].filter(Boolean).join(" ").toLowerCase();
    return alvo.includes(q);
  });

  const osSemContratoFiltradas = osSemContrato.filter((os) => {
    if (!dentroDoIntervalo(os.Cabecalho?.dDtPrevisao, dataInicioOS, dataFimOS)) return false;
    if (!q) return true;
    const alvo = [os.Cabecalho?.cNumOS, os.cliente_nome, os.vendedor_nome]
      .filter(Boolean).join(" ").toLowerCase();
    return alvo.includes(q);
  });

  const valorTotalMensal = contratosFiltrados.reduce((acc, c) => acc + (c.cabecalho?.nValTotMes || 0), 0);
  const totalOsVinculadas = contratosFiltrados.reduce((acc, c) => {
    const osDoContrato = (c.os_vinculadas || []).filter((os) =>
      dentroDoIntervalo(os.Cabecalho?.dDtPrevisao, dataInicioOS, dataFimOS)
    );
    return acc + osDoContrato.length;
  }, 0);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin")}
            className="p-1.5 rounded-lg hover:bg-white/10 transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-lg font-bold tracking-tight">Contratos e OS · OMIE</span>
        </div>
        <button onClick={() => navigate("/inicio")} style={{
          display: "flex", alignItems: "center", gap: 6,
          background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
          borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
        }}>
          Menu Principal
        </button>
      </header>

      <main className="flex-1 px-6 py-8 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-6 bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-3">
          <p className="text-xs text-gray-500">
            {status?.status === "executando" && "Sincronizando clientes com a OMIE…"}
            {status?.status === "concluido" && status?.finalizado_em &&
              `Clientes sincronizados em: ${new Date(status.finalizado_em).toLocaleString("pt-BR")}`}
            {status?.status === "erro" && `Falha ao sincronizar clientes: ${status.erro}`}
            {(!status || status.status === "nunca_executado") && "Clientes ainda não sincronizados com a OMIE."}
          </p>
          <button
            onClick={sincronizar}
            disabled={sincronizando || status?.status === "executando"}
            className="flex items-center gap-1.5 text-sm font-semibold text-white bg-purple-700 hover:bg-purple-800 disabled:opacity-50 px-4 py-1.5 rounded-lg transition"
          >
            <RefreshCw className={`w-4 h-4 ${(sincronizando || status?.status === "executando") ? "animate-spin" : ""}`} />
            Sincronizar clientes
          </button>
        </div>

        {!loading && !erro && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={<FileText className="w-6 h-6 text-white" />}
              label="Contratos Ativos"
              value={contratosFiltrados.length}
              color="bg-purple-600"
            />
            <StatCard
              icon={<Briefcase className="w-6 h-6 text-white" />}
              label="OS vinculadas a contrato"
              value={totalOsVinculadas}
              color="bg-emerald-500"
            />
            <StatCard
              icon={<Briefcase className="w-6 h-6 text-white" />}
              label="OS sem contrato"
              value={osSemContratoFiltradas.length}
              color="bg-amber-500"
            />
            <StatCard
              icon={<FileText className="w-6 h-6 text-white" />}
              label="Valor mensal total"
              value={formatMoeda(valorTotalMensal)}
              color="bg-pink-500"
            />
          </div>
        )}


        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex flex-col gap-2">
            <div
              className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5"
              title="Filtra a lista de contratos pela vigência final (dVigFinal)."
            >
              <span className="font-semibold text-gray-600 shrink-0">Contrato</span>
              <input
                type="date"
                value={dataInicioContrato}
                onChange={(e) => setDataInicioContrato(e.target.value)}
                className="text-sm border border-gray-200 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400"
              />
              <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <input
                type="date"
                value={dataFimContrato}
                onChange={(e) => setDataFimContrato(e.target.value)}
                className="text-sm border border-gray-200 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400"
              />
            </div>
            <div
              className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5"
              title="Filtra as OS exibidas (sem contrato e dentro de cada contrato) pela data de previsão (dDtPrevisao)."
            >
              <span className="font-semibold text-gray-600 shrink-0" style={{ paddingRight: '35px' }}>OS</span>
              <input
                type="date"
                value={dataInicioOS}
                onChange={(e) => setDataInicioOS(e.target.value)}
                className="text-sm border border-gray-200 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400"
              />
              <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <input
                type="date"
                value={dataFimOS}
                onChange={(e) => setDataFimOS(e.target.value)}
                className="text-sm border border-gray-200 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="text"
              placeholder="Buscar contrato, OS ou cliente…"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-pink-400 w-64"
            />
            <button
              onClick={limparFiltros}
              style={{ fontSize: 13, color: 'red', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              Limpar Filtros
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <h2 className="text-base font-semibold text-gray-800">Contratos</h2>
        </div>

        {loading && (
          <div className="py-16 text-center text-gray-400 text-sm">Carregando dados da OMIE…</div>
        )}

        {erro && (
          <div className="py-12 text-center bg-white rounded-2xl border border-gray-100">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-sm text-red-500">{erro}</p>
          </div>
        )}

        {!loading && !erro && (
          <div className="flex flex-col gap-3">
            {contratosFiltrados.map((c) => (
              <ContratoCard
                key={`${c.empresa}-${c.cabecalho?.cNumCtr}`}
                contrato={c}
                osDataInicio={dataInicioOS}
                osDataFim={dataFimOS}
              />
            ))}
            {contratosFiltrados.length === 0 && (
              <div className="py-10 text-center text-gray-400 text-sm bg-white rounded-2xl border border-gray-100">
                Nenhum contrato encontrado.
              </div>
            )}
          </div>
        )}

        {!loading && !erro && osSemContratoFiltradas.length > 0 && (
          <div className="mt-10">
            <h2 className="text-base font-semibold text-gray-800 mb-4">OS sem contrato vinculado</h2>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">OS</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendedor</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Previsão</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Valor</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Faturamento</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {osSemContratoFiltradas.map((os) => (
                    <OSLinha key={`${os.empresa}-${os.Cabecalho?.nCodOS}`} os={os} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      <footer className="text-center text-xs text-gray-400 py-6">
        © {new Date().getFullYear()} Youx Group · Contratos e OS OMIE
      </footer>
    </div>
  );
}
